import { CheckCircle, Users, Globe, Heart, Target, Clock, Loader } from 'lucide-react'
import { useState, useEffect } from 'react'
import OptimizedImage from './OptimizedImage'
import { homepageService } from '../lib/homepageService'

const AboutSection = () => {
  const [aboutImage, setAboutImage] = useState<string>('')
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    const loadAboutImage = async () => {
      try {
        const aboutData = await homepageService.getAbout()
        if (aboutData?.imageUrl) {
          setAboutImage(aboutData.imageUrl)
        } else {
          // Fallback to default image
          setAboutImage('https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80')
        }
      } catch (error) {
        console.error('Error loading about image:', error)
        setAboutImage('https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80')
      } finally {
        setLoading(false)
      }
    }
    
    loadAboutImage()
  }, [])
  
  const features = [
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Atuação Nacional',
      description: 'Cobrimos todos os estados dos EUA com comunidades locais e suporte regional.',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Rede de Apoio',
      description: 'Conectamos você com brasileiros experientes que podem oferecer orientação prática.',
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Ajuda Humanitária',
      description: 'Mais de 1 milhão de pessoas assistidas em situações de vulnerabilidade.',
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Foco em Resultados',
      description: 'Nossas iniciativas têm como objetivo real a integração e sucesso da comunidade.',
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: '10+ Anos de Experiência',
      description: 'Uma década dedicada a conectar e empoderar brasileiros nos Estados Unidos.',
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Plataforma Verificada',
      description: 'Todos os anúncios e grupos passam por moderação para garantir segurança.',
    },
  ]

  return (
    <section id="sobre" className="w-full py-12 md:py-20 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Left column - Image and highlights */}
          <div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              {loading ? (
                <div className="w-full h-[400px] md:h-[500px] bg-noe-900/30 flex items-center justify-center">
                  <Loader className="w-12 h-12 text-noe-brand animate-spin" />
                </div>
              ) : (
                <OptimizedImage
                  src={aboutImage}
                  alt="Comunidade Grupo Noé"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                  width={800}
                  height={500}
                />
              )}
                <div className="absolute inset-0 bg-gradient-to-t from-noe-900/70 via-noe-800/50 to-noe-900/30" />
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-noe-900/80 via-noe-800/60 to-noe-900/40">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-noe-brand/20 rounded-full">
                    <Users className="w-8 h-8 text-noe-brand" />
                  </div>
                  <div>
                      <div className="text-4xl font-bold text-white drop-shadow-lg">1M+</div>
                     <div className="text-white drop-shadow">Pessoas ajudadas</div>
                  </div>
                </div>
                  <p className="text-white text-lg drop-shadow-lg">
                   Uma década transformando vidas e construindo pontes entre Brasil e Estados Unidos.
                 </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="bg-noe-800/30 border border-noe-700/50 rounded-xl p-5">
                <div className="text-2xl font-bold text-noe-brand mb-1">50+</div>
                <div className="text-noe-300 text-sm">Cidades atendidas</div>
              </div>
              <div className="bg-noe-800/30 border border-noe-700/50 rounded-xl p-5">
                <div className="text-2xl font-bold text-noe-brand mb-1">24/7</div>
                <div className="text-noe-300 text-sm">Suporte disponível</div>
              </div>
            </div>
          </div>
          
          {/* Right column - Content */}
          <div>
            <div className="mb-6">
              <span className="inline-block px-4 py-1.5 bg-noe-brand/10 text-noe-brand rounded-full text-sm font-medium mb-4">
                Sobre o Grupo Noé
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-noe-100 mb-6">
                Mais do que uma comunidade, uma <span className="text-noe-brand">família</span> no exterior
              </h2>
              <p className="text-lg text-noe-300 mb-6">
                O <strong>Grupo Noé</strong> nasceu da necessidade de criar uma rede de apoio sólida para brasileiros que chegam aos Estados Unidos. 
                Começamos pequeno, com um grupo de amigos ajudando outros compatriotas, e hoje somos a maior plataforma de conexão e suporte 
                para a comunidade brasileira nos EUA.
              </p>
              <p className="text-lg text-noe-300 mb-8">
                Nossa missão é facilitar a adaptação, oferecer oportunidades reais e construir um ambiente onde cada brasileiro se sinta 
                acolhido, informado e empoderado para conquistar seus objetivos.
              </p>
            </div>
            
            <div className="mb-10">
               <h3 className="text-xl font-bold text-noe-100 mb-6">Como funcionamos</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 bg-noe-800/20 rounded-xl border border-noe-700/30 hover:border-noe-brand/30 transition-colors"
                  >
                    <div className="p-2 bg-noe-brand/10 text-noe-brand rounded-lg">
                      {feature.icon}
                    </div>
                    <div>
                       <h4 className="font-semibold text-noe-100 mb-1">{feature.title}</h4>
                      <p className="text-sm text-noe-300">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-noe-800/30 to-noe-900/30 border border-noe-700/50 rounded-2xl p-6 md:p-8">
               <h3 className="text-xl font-bold text-noe-100 mb-4">Nossos pilares</h3>
              <ul className="space-y-3">
                {[
                  'Transparência em todas as nossas ações e comunicações',
                  'Respeito à diversidade e individualidade de cada membro',
                  'Compromisso com resultados tangíveis para a comunidade',
                  'Inovação constante para melhor atender às necessidades',
                  'Colaboração entre membros para fortalecer a rede',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-noe-brand mt-0.5 flex-shrink-0" />
                    <span className="text-noe-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button className="px-8 py-3 bg-noe-brand text-noe-950 font-bold rounded-lg hover:bg-noe-brand/90 transition-colors">
                Conheça nossa história completa
              </button>
              <button className="px-8 py-3 border border-noe-brand text-noe-brand font-bold rounded-lg hover:bg-noe-brand/10 transition-colors">
                Fale conosco
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AboutSection